/**
 * The media a post was published with.
 *
 * Media selection helper routines for the post composer. These helpers are
 * the single source of truth for resolving published media and preparing it
 * for submission.
 */
import { POST_IMAGE_LAYOUT, POST_MEDIA_TYPE } from '../constants';
import type { FeedPost, MediaFile, PostImageLayout } from '../types';
import { resolveLayoutForAlbum } from './postImageLayout';

/** The media half of the composer state. */
export interface PostMediaSelection {
  media: MediaFile | null;
  mediaFiles: MediaFile[];
  imageLayout: PostImageLayout | null;
}

const NO_MEDIA: PostMediaSelection = {
  media: null,
  mediaFiles: [],
  imageLayout: null,
};

/** The API has no GIF flag on the post itself — the URL is the only signal. */
const isGifUri = (uri: string): boolean => {
  const value = uri.toLowerCase();
  return value.endsWith('.gif') || value.includes('giphy.com');
};

/** Post media arrives as a bare URL, as `{ uri }`, or as a bundled asset. */
const readUri = (source: unknown): string => {
  if (typeof source === 'string') return source.trim();
  if (source && typeof source === 'object' && 'uri' in source) {
    const { uri } = source as { uri?: unknown };
    return typeof uri === 'string' ? uri.trim() : '';
  }
  return '';
};

export const resolvePublishedPostMedia = (
  post?: FeedPost | null,
): PostMediaSelection => {
  if (!post) return NO_MEDIA;

  const videoUri = readUri(post.video);
  if (videoUri) {
    return {
      media: {
        uri: videoUri,
        mediaType: POST_MEDIA_TYPE.VIDEO,
        width: post.videoWidth ?? post.video?.width,
        height: post.videoHeight ?? post.video?.height,
        type: 'video/mp4',
      },
      mediaFiles: [],
      imageLayout: null,
    };
  }

  const album = Array.isArray(post.images)
    ? post.images.map(readUri).filter(Boolean)
    : [];

  if (album.length > 1) {
    const files: MediaFile[] = album.map(uri => ({
      uri,
      mediaType: POST_MEDIA_TYPE.IMAGE,
      type: 'image/jpeg',
    }));
    return {
      media: files[0],
      mediaFiles: files,
      // The layout the post carries wins. Guessing from the count alone turns a
      // three-photo gallery into an incomplete Comparison4, which then fails
      // validation and blocks an otherwise text-only edit.
      imageLayout: post.imageLayout ?? resolveLayoutForAlbum(files.length, null),
    };
  }

  const imageUri = readUri(post.image) || album[0] || '';
  if (!imageUri) return NO_MEDIA;

  const gif = isGifUri(imageUri);
  const image: MediaFile = {
    uri: imageUri,
    mediaType: gif ? POST_MEDIA_TYPE.GIF : POST_MEDIA_TYPE.IMAGE,
    type: gif ? 'image/gif' : 'image/jpeg',
  };
  return {
    media: image,
    mediaFiles: [image],
    imageLayout: POST_IMAGE_LAYOUT.SINGLE,
  };
};

export interface SubmittedPostMediaInput {
  isEditing: boolean;
  /** The post being edited, as it was published. */
  publishedPost: FeedPost | null;
  /** What the composer currently holds. */
  composerMedia: PostMediaSelection;
  honorComposerMedia?: boolean;
}

/**
 * The media a submit may carry.
 *
 * By default a published post's media stays frozen and the composer is ignored.
 * Mobile passes `honorComposerMedia` so a removal is what gets saved.
 */
export const resolveSubmittedPostMedia = ({
  isEditing,
  publishedPost,
  composerMedia,
  honorComposerMedia = false,
}: SubmittedPostMediaInput): PostMediaSelection =>
  !isEditing || honorComposerMedia
    ? composerMedia
    : resolvePublishedPostMedia(publishedPost);
